import { useRef } from 'react';

import { haptic } from '@/lib/telegram';

// SPEC-021: Tiny <audio> wrapper that reports progress to the heartbeat
// loop. Uses native controls so Telegram's media stack stays in charge of
// rendering + system-level playback concerns (lock screen, background).

interface AudioPlayerProps {
  src: string;
  title?: string;
  durationSec?: number;
  /** Called with a 0–100 percent whenever the audio's currentTime advances. */
  onProgress?: (progress: number) => void;
}

export function AudioPlayer({ src, title, durationSec, onProgress }: AudioPlayerProps) {
  const ref = useRef<HTMLAudioElement>(null);

  const handleTimeUpdate = () => {
    const el = ref.current;
    if (!el || !el.duration || !isFinite(el.duration)) return;
    const percent = (el.currentTime / el.duration) * 100;
    onProgress?.(percent);
  };

  return (
    <div className="rounded-xl bg-[var(--color-secondary-bg)] p-3">
      {title && (
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <span>🎧</span>
          <span className="truncate">{title}</span>
          {typeof durationSec === 'number' && durationSec > 0 && (
            <span className="ml-auto text-xs text-[var(--color-muted)]">
              {formatDuration(durationSec)}
            </span>
          )}
        </div>
      )}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={ref}
        src={src}
        controls
        preload="metadata"
        className="w-full"
        onPlay={() => haptic('selection')}
        onTimeUpdate={handleTimeUpdate}
      />
    </div>
  );
}

function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, '0')}`;
}
